import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { AuthContext } from '../contexts/AuthContext';
import './QuantumPurchase.css';

const QuantumPurchase = ({ creditPackage, onSuccess, onClose }) => {
  const { user, updateUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [taxAmount, setTaxAmount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(creditPackage.price);
  const [userCountry, setUserCountry] = useState('IN'); // Default to India

  // Detect user's country
  useEffect(() => {
    detectUserCountry();
    calculateTaxes(creditPackage.price);
  }, [creditPackage]);

  const detectUserCountry = async () => {
    try {
      // Use IP geolocation service
      const response = await axios.get('https://ipapi.co/json/');
      setUserCountry(response.data.country_code);
    } catch (error) {
      console.error('Failed to detect country:', error);
      // Fallback to browser language or default
      const browserLang = navigator.language || navigator.userLanguage;
      if (browserLang.includes('US') || browserLang.includes('EN')) {
        setUserCountry('US');
      }
    }
  };

  const calculateTaxes = (basePrice) => {
    // Simple tax calculation based on country
    let taxRate = 0;
    if (userCountry === 'IN') { // India
      taxRate = 0.18; // 18% GST
    } else if (userCountry === 'US') { // United States
      taxRate = 0.08; // Average sales tax ~8%
    } else if (userCountry === 'CA') { // Canada
      taxRate = 0.13; // 13% HST
    } else if (['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 
                'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 
                'RO', 'SK', 'SI', 'ES', 'SE'].includes(userCountry)) { // European Union
      taxRate = 0.21; // Average VAT ~21%
    }
    
    const calculatedTax = basePrice * taxRate;
    setTaxAmount(calculatedTax);
    setTotalAmount(basePrice + calculatedTax);
  };

  const initiatePurchase = async () => {
    setLoading(true);
    setError('');

    try {
      // Step 1: Pre-purchase validation
      const validationResponse = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/validation/purchase`,
        {
          userId: user._id,
          packageId: creditPackage._id,
          amount: totalAmount,
          currency: userCountry === 'IN' ? 'INR' : 'USD'
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!validationResponse.data.valid) {
        throw new Error(validationResponse.data.reason || 'Purchase validation failed');
      }

      // Step 2: Create payment order on backend
      const orderResponse = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/payments/create-order`,
        {
          userId: user._id,
          userEmail: user.email,
          packageId: creditPackage._id,
          amount: totalAmount,
          currency: userCountry === 'IN' ? 'INR' : 'USD',
          country: userCountry
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      const orderData = orderResponse.data.data;

      // Step 3: Route to appropriate payment processor
      if (userCountry === 'IN') {
        await initiateRazorpayPayment(orderData);
      } else {
        await initiatePaypalPayment(orderData);
      }

    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Payment failed');
      setLoading(false);
    }
  };

  const initiateRazorpayPayment = (orderData) => {
    return new Promise((resolve, reject) => {
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: orderData.amount * 100, // Convert to paise
        currency: orderData.currency,
        name: "Quantum Analytics",
        description: `Purchase of ${creditPackage.name}`,
        order_id: orderData.gatewayOrderId,
        handler: async function(response) {
          try {
            await verifyPayment(response, orderData._id);
            resolve();
          } catch (err) {
            reject(err);
          }
        },
        prefill: {
          name: user.name,
          email: user.email,
        },
        theme: {
          color: "#2c5aa0"
        },
        modal: {
          ondismiss: function() {
            setLoading(false);
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    });
  };

  const initiatePaypalPayment = async (orderData) => {
    try {
      // For PayPal, we typically redirect to their site
      // This would be handled differently based on your PayPal integration
      window.location.href = `${process.env.REACT_APP_API_BASE_URL}/api/payments/paypal/create?orderId=${orderData._id}&userId=${user._id}`;
    } catch (err) {
      throw new Error('Failed to initialize PayPal payment');
    }
  };

  const verifyPayment = async (paymentResponse, orderId) => {
    try {
      const verificationResponse = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/payments/verify`,
        {
          orderId,
          paymentId: paymentResponse.razorpay_payment_id,
          signature: paymentResponse.razorpay_signature,
          userId: user._id
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (verificationResponse.data.success) {
        // Update user credits
        await updateUser();
        
        // Show success and close modal
        if (onSuccess) onSuccess(verificationResponse.data.data);
      } else {
        throw new Error('Payment verification failed');
      }
    } catch (err) {
      throw new Error(err.response?.data?.error || 'Payment verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="quantum-purchase-modal">
      <div className="quantum-purchase-content">
        <button className="close-button" onClick={onClose}>×</button>
        
        <h2>Purchase {creditPackage.name}</h2>
        
        {error && (
          <div className="error-message">
            {error}
            <button onClick={() => setError('')}>Dismiss</button>
          </div>
        )}
        
        <div className="purchase-details">
          <div className="detail-row">
            <span>Package:</span>
            <span>{creditPackage.name} ({creditPackage.credits} credits)</span>
          </div>
          <div className="detail-row">
            <span>Base Price:</span>
            <span>${creditPackage.price.toFixed(2)}</span>
          </div>
          <div className="detail-row">
            <span>Taxes ({userCountry}):</span>
            <span>${taxAmount.toFixed(2)}</span>
          </div>
          <div className="detail-row total">
            <span>Total:</span>
            <span>${totalAmount.toFixed(2)}</span>
          </div>
          <div className="detail-row">
            <span>Payment Method:</span>
            <span>{userCountry === 'IN' ? 'Razorpay (Cards, UPI, Netbanking)' : 'PayPal'}</span>
          </div>
        </div>
        
        <div className="action-buttons">
          <button 
            onClick={initiatePurchase} 
            disabled={loading}
            className="purchase-button"
          >
            {loading ? 'Processing...' : `Purchase - $${totalAmount.toFixed(2)}`}
          </button>
          <button onClick={onClose} className="cancel-button">
            Cancel
          </button>
        </div>
        
        <div className="security-notice">
          <p>🔒 Your payment details are secured with {userCountry === 'IN' ? 'Razorpay' : 'PayPal'}</p>
          <p>Your credits will be added immediately after successful payment</p>
        </div>
      </div>
    </div>
  );
};

export default QuantumPurchase;